import type { PaymentBrief } from "@/features/finance/types";
import type { FeeStatus } from "@/features/drivers/types";
/** Backend serializer'lariga mos turlar: apps/budget/{serializers,services}.py. Decimal'lar string bo'lib keladi. */

export type Money = string | number;

export interface PlanTotals {
  expenses: Money;
  salaries: Money;
  bonuses: Money;
  total: Money;
  exists: boolean;
}

export interface ActualTotals {
  expenses: Money;
  expenses_pending: Money;
  salaries: Money;
  bonuses: Money;
  bonuses_draft: Money;
  total: Money;
}

export type BudgetCategory = "expenses" | "salaries" | "bonuses";

export interface BudgetSummary {
  branch_id: number | null;
  year: number;
  month: number;
  plan: PlanTotals;
  actual: ActualTotals;
  hires: number;
  cost_per_hire: number | null;
  used_pct: number | null;
  used_by_category: Record<BudgetCategory, number | null>;
  over_budget: boolean;
  over_categories: BudgetCategory[];
}

export interface BranchSummary extends BudgetSummary {
  branch: string;
}

export interface BudgetAlert {
  kind: "over_budget" | "over_category" | "pending_expenses" | "draft_bonuses" | string;
  branch_id: number;
  branch: string;
  message: string;
  link?: string;
}

export interface BudgetOverview {
  year: number;
  month: number;
  total: BudgetSummary;
  branches: BranchSummary[];
  alerts: BudgetAlert[];
}

export interface BudgetPlan {
  id: number;
  branch: number;
  branch_name: string;
  year: number;
  month: number;
  planned_expenses: Money;
  planned_salaries: Money;
  planned_bonuses: Money;
  planned_total: Money;
  currency: string;
  notes: string;
  version: number;
}

export interface PlanUpsertItem {
  branch: number;
  year: number;
  month: number;
  planned_expenses: Money;
  planned_salaries: Money;
  planned_bonuses: Money;
  currency?: string;
  notes?: string;
  /** Ko'rilgan reja versiyasi: boshqa birov o'zgartirgan bo'lsa server 409 qaytaradi (yangi reja uchun null) */
  version?: number | null;
}

export interface BudgetBranch {
  id: number;
  name: string;
}

export type ExpenseStatus = "pending" | "approved" | "rejected";

export interface UserRef {
  id: number;
  display_name: string;
  initials: string;
}

export interface Expense {
  id: number;
  branch: number;
  branch_name: string;
  date: string;
  category: string;
  description: string;
  amount: Money;
  currency: string;
  receipt_url: string | null;
  receipt_name: string;
  status: ExpenseStatus;
  submitted_by: UserRef | null;
  approval: number | null;
  decided_by: UserRef | null;
  decided_at: string | null;
  rejection_reason: string;
  /** To'langan bo'lsa -- qaysi hisobdan (Pul bo'limi) */
  payment: PaymentBrief | null;
  created_at: string;
  version: number;
}

export interface ExpenseSummary {
  plan: Money;
  actual: Money;
  pending: Money;
  used_pct: number | null;
  categories: string[];
  receipt_required: boolean;
}

export interface SalaryUser extends UserRef {
  full_name: string;
}

export interface SalaryRow {
  id: number | null;
  user: SalaryUser;
  role: string;
  branch: number | null;
  branch_name: string | null;
  year: number;
  month: number;
  amount: Money | null;
  currency: string;
  paid: boolean;
  paid_at: string | null;
  note: string;
  payment?: PaymentBrief | null;
  version: number | null;
  suggested: Money | null;
  /** Joriy foydalanuvchi bu qatorni o'zgartira oladimi (menejer o'z maoshini emas) */
  editable: boolean;
}

/** SalarySerializer: upsert javobi */
export interface Salary {
  id: number;
  user: UserRef;
  role: string;
  branch: number | null;
  branch_name: string | null;
  year: number;
  month: number;
  amount: Money;
  currency: string;
  paid: boolean;
  paid_at: string | null;
  note: string;
  version: number;
}

export interface SalariesResponse {
  year: number;
  month: number;
  results: SalaryRow[];
  /** Valyuta bo'yicha jami (USD va UZS qo'shilmaydi) */
  totals: { currency: string; amount: Money }[];
  count: number;
  paid_count: number;
}

export type BonusStatus = "draft" | "approved" | "paid";

export interface BonusRule {
  per_hire: number;
  per_30: number;
  per_60: number;
  per_90: number;
}

export interface BonusDetail {
  driver_id: number;
  driver: string;
  hired: string;
  start: string;
  retained_30: boolean;
  retained_60: boolean;
  retained_90: boolean;
}

export interface Bonus {
  id: number;
  user: UserRef;
  branch: number | null;
  branch_name: string | null;
  year: number;
  month: number;
  /** 1 -- oyning asosiy bonusi; 2+ -- qo'shimcha: tasdiqlangan bonusdan keyin tasdiqlangan hire va retention'lar */
  part: number;
  hires: number;
  retained_30: number;
  retained_60: number;
  retained_90: number;
  rule_name: string;
  rule: Partial<BonusRule>;
  calculated_amount: Money;
  adjustment: Money;
  adjustment_note: string;
  total: Money;
  currency: string;
  status: BonusStatus;
  details: BonusDetail[];
  approved_by: UserRef | null;
  approved_at: string | null;
  paid_at: string | null;
  payment?: PaymentBrief | null;
  version: number;
}

export interface BonusSummary {
  year: number;
  month: number;
  count: number;
  draft: number;
  approved: number;
  paid: number;
  /** USD qiymati (oy boshi kursi); valyuta bo'yicha -- `totals` */
  total: Money;
  totals: { currency: string; amount: Money }[];
  rule: {
    bonus_rule_name: string;
    bonus_per_hire: number;
    bonus_per_30_days: number;
    bonus_per_60_days: number;
    bonus_per_90_days: number;
    bonus_currency: string;
  };
}

export const EXPENSE_STATUS_LABEL: Record<ExpenseStatus, string> = { pending: "Pending", approved: "Approved", rejected: "Rejected" };
export const BONUS_STATUS_LABEL: Record<BonusStatus, string> = { draft: "Draft", approved: "Approved", paid: "Paid" };
export const CURRENCIES = ["USD", "UZS"];

export function money(value: Money | null | undefined): number {
  if (value === null || value === undefined || value === "") return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

/** Carrier to'lovi: ishga olingan joylashtirish (backend: /api/budget/placements/) */
export interface PlacementFee {
  id: number;
  driver: { id: number; full_name: string; status: string };
  owner: { id: number; display_name: string } | null;
  branch_name: string | null;
  carrier: { id: number; name: string };
  job: { id: number; title: string };
  start_date: string | null;
  pay_plan: string;
  fee_status: FeeStatus;
  fee_amount: Money | null;
  fee_due_date: string | null;
  fee_paid_at: string | null;
  fee_note: string;
  overdue: boolean;
  /** Qaysi hisobga qancha tushgan (Pul bo'limi) */
  received: PaymentBrief[];
  /** Tushgan jami (USD) va qolgan summa (summa noma'lum bo'lsa null) */
  received_total: Money;
  remaining: Money | null;
  /** Hire menejer tomonidan tasdiqlanganmi -- tasdiqlanmaguncha pul qabul qilinmaydi */
  approved: boolean;
  /** Daftarsiz "to'langan"/"qisman" (Sheets importi yoki eski belgi): tushum yozuvi yo'q */
  legacy: boolean;
  version: number;
}

export interface PlacementSummary {
  due: number;
  overdue: number;
  paid: number;
  partial: number;
  not_paid: number;
  approval_pending: number;
  paid_sum: Money;
  expected_sum: Money;
}

export interface PlacementsResponse {
  count: number;
  page: number;
  pages: number;
  page_size: number;
  results: PlacementFee[];
  summary: PlacementSummary;
}

